import { Link } from "react-router-dom";
import { IoCopyOutline } from "react-icons/io5";
import { Controller, useForm } from "react-hook-form";
import { MdCreditCard, MdOutlinePix } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { PlanDto } from "~/data/subscription/plan.dto";

import {
  createSubscription,
  getSubscription,
  type CreateSubscriptionDto,
} from "~/data/subscription/subscription.service";

import { Field } from "../shared/field";
import { Input } from "../shared/input";
import { Button } from "../shared/button";
import { Select } from "../shared/select";
import { mask } from "../shared/utils/mask";
import asaasLogo from "../shared/assets/asaas.png";
import { IconButton } from "../shared/icon-button";
import logoExtended from "../shared/assets/logo-full.png";
import partyPopper from "../shared/assets/party-popper.png";

interface CheckoutProps {
  product: PlanDto;
  initialState?: any;
}

export function Checkout({ product, initialState }: CheckoutProps) {
  const queryClient = useQueryClient();
  const form = useForm<CreateSubscriptionDto>({
    defaultValues: {
      billing_type:
        initialState?.billing_type ||
        (product.is_installment ? "credit_card" : undefined),
      subscription_id: product.id,
    },
  });
  const mutation = useMutation({
    mutationFn: (dto: CreateSubscriptionDto) => createSubscription(dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["user"] }),
  });
  const currentSubscription = mutation.data?.user_subscription ?? initialState;
  const onSubmit = form.handleSubmit((data) => mutation.mutate(data));

  const billingType = form.watch("billing_type");
  const hasBillingType = !!billingType;

  const { data: details } = useQuery({
    queryKey: ["subscription_details"],
    queryFn: async () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      return await getSubscription();
    },
    refetchInterval: 3000, // 3s
    enabled: (q) => {
      if (!currentSubscription) return false;
      const payment = q.state.data?.user_subscription;
      if (!payment) return true;
      return payment.billing_type === "pix" && payment.status !== "paid";
    },
  });

  const subscription = details?.user_subscription ?? currentSubscription;
  const isPendingPixPayment =
    subscription?.billing_type === "pix" &&
    subscription.status &&
    subscription.status !== "paid";
  const isPaymentFinished = !!subscription && !isPendingPixPayment;

  let step = 0;
  if (hasBillingType) step++;
  if (isPaymentFinished) step++;

  return (
    <form onSubmit={onSubmit}>
      <img
        src={logoExtended}
        alt=""
        height={95}
        className="mb-5"
        style={{ height: 95 }}
      />

      <div id="steps" className="flex gap-2 py-5 mb-2 justify-center">
        <span
          data-active={step >= 0}
          className="w-5 h-5 rounded-full bg-gray-100 data-[active=true]:bg-alternative-2"
        />
        <span
          data-active={step >= 1}
          className="w-5 h-5 rounded-full bg-gray-100 data-[active=true]:bg-alternative-2"
        />
        <span
          data-active={step >= 2}
          className="w-5 h-5 rounded-full bg-gray-100 data-[active=true]:bg-alternative-2"
        />
      </div>

      {isPaymentFinished ? (
        <div>
          <h2 className="font-alternative text-center font-semibold text-2xl uppercase mb-6">
            Pagamento Finalizado
          </h2>
          <div
            className="flex flex-col items-center py-10 gap-10 mx-auto"
            style={{ maxWidth: 520 }}
          >
            <div className="text-5xl">
              <img src={partyPopper} alt="" className="h-20 w-20" />
            </div>
            <h3 className="text-alternative-2 text-center text-3xl font-semibold font-alternative">
              Seu pagamento foi realizado
              <br />
              com sucesso!
            </h3>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="font-alternative text-center font-semibold text-2xl uppercase mb-6">
            Selecione o tipo de pagamento
          </h2>

          <div className="flex gap-5 mb-6">
            <Controller
              control={form.control}
              name="billing_type"
              render={({ field }) => (
                <Button
                  variant="primary-1"
                  type="button"
                  className="h-28 max-w-full flex-1"
                  style={{ width: 370 }}
                  inverted={field.value === "credit_card"}
                  onClick={() => field.onChange("credit_card")}
                >
                  <MdCreditCard size={24} />
                  Cartão de Crédito
                </Button>
              )}
            />
            {!product.is_installment && (
              <Controller
                control={form.control}
                name="billing_type"
                render={({ field }) => (
                  <Button
                    variant="secondary-1"
                    type="button"
                    className="h-28 max-w-full flex-1"
                    style={{ width: 370 }}
                    inverted={field.value === "pix"}
                    onClick={() => field.onChange("pix")}
                  >
                    <MdOutlinePix size={24} />
                    Pix
                  </Button>
                )}
              />
            )}
          </div>

          {billingType === "credit_card" && (
            <div className="space-y-5 mb-5 px-5">
              <Field label="CPF/CNPJ">
                <Input
                  {...form.register("user_info.cpf_cnpj")}
                  inputMode="numeric"
                  onInput={mask(formatters.cpfCnpj)}
                />
              </Field>

              <Field label="Número do cartão">
                <Input
                  {...form.register("payment_info.credit_card.number")}
                  inputMode="numeric"
                  onInput={mask((value) => {
                    const digits = value.replace(/\D/g, "");

                    const isAmex = /^3[47]/.test(digits);
                    const isDiners = /^30[0-5]|^36|^38/.test(digits);

                    if (isAmex) {
                      return digits
                        .slice(0, 15) // Limita a 15 dígitos
                        .replace(/(\d{4})(\d{6})(\d{0,5})/, "$1 $2 $3")
                        .trim();
                    }
                    if (isDiners) {
                      return digits
                        .slice(0, 14) // Limita a 14 dígitos
                        .replace(/(\d{4})(\d{6})(\d{0,4})/, "$1 $2 $3")
                        .trim();
                    }

                    return digits
                      .slice(0, 16) // Limita a 16 dígitos
                      .replace(/(\d{4})/g, "$1 ")
                      .trim();
                  })}
                />
              </Field>
              <div className="flex flex-wrap gap-x-9 gap-y-5">
                <Field label="Validade mês/ano" className="flex-1">
                  <div className="flex gap-6">
                    <Input
                      {...form.register(
                        "payment_info.credit_card.expiry_month",
                        {
                          onChange(e) {
                            if (e.currentTarget.value.length === 2) {
                              form.setFocus(
                                "payment_info.credit_card.expiry_year"
                              );
                            }
                          },
                        }
                      )}
                      placeholder="MM"
                      inputMode="numeric"
                      onInput={mask((value) =>
                        value.replace(/\D/g, "").slice(0, 2)
                      )}
                    />
                    <Input
                      {...form.register(
                        "payment_info.credit_card.expiry_year",
                        {
                          onChange(e) {
                            if (e.currentTarget.value.length === 2) {
                              form.setFocus("payment_info.credit_card.ccv");
                            }
                          },
                        }
                      )}
                      placeholder="AA"
                      inputMode="numeric"
                      onInput={mask((value) =>
                        value.replace(/\D/g, "").slice(0, 2)
                      )}
                    />
                  </div>
                </Field>
                <Field label="CCV">
                  <Input
                    {...form.register("payment_info.credit_card.ccv", {
                      onChange(e) {
                        if (e.currentTarget.value.length === 4) {
                          form.setFocus("payment_info.credit_card.holder_name");
                        }
                      },
                    })}
                    inputMode="numeric"
                    onInput={mask((value) =>
                      value.replace(/\D/g, "").slice(0, 4)
                    )}
                  />
                </Field>
              </div>

              <Field label="Nome do titular">
                <Input
                  {...form.register("payment_info.credit_card.holder_name")}
                />
              </Field>
              <Field label="E-mail do titular">
                <Input
                  {...form.register(
                    "payment_info.credit_card_holder_info.email"
                  )}
                />
              </Field>
              <Field label="CPF/CNPJ do titular">
                <Input
                  {...form.register(
                    "payment_info.credit_card_holder_info.cpf_cnpj"
                  )}
                  inputMode="numeric"
                  onInput={mask(formatters.cpfCnpj)}
                />
              </Field>
              <Field label="CEP do titular">
                <Input
                  {...form.register(
                    "payment_info.credit_card_holder_info.postal_code"
                  )}
                  inputMode="numeric"
                  onInput={mask(formatters.cep)}
                />
              </Field>
              <Field label="Número do endereço do titular">
                <Input
                  {...form.register(
                    "payment_info.credit_card_holder_info.address_number"
                  )}
                  inputMode="numeric"
                  onInput={mask(formatters.cep)}
                />
              </Field>
              <Field label="Celular do titular">
                <Input
                  {...form.register(
                    "payment_info.credit_card_holder_info.mobile_phone"
                  )}
                  inputMode="numeric"
                  onInput={mask(formatters.phone)}
                />
              </Field>

              {product.is_installment && (
                <Field label="Parcelas">
                  <Select {...form.register("installment_count")}>
                    <option>1</option>
                    <option>2</option>
                    <option>3</option>
                    <option>4</option>
                  </Select>
                </Field>
              )}
            </div>
          )}

          {billingType === "pix" &&
            (isPendingPixPayment ? (
              <div className="grid gap-5 py-5 text-center mx-auto w-3/4">
                <h4 className="font-alternative text-2xl font-semibold uppercase text-primary-1">
                  Pix
                </h4>
                <p className="text-[#666] font-alternative font-semibold leading-7 text-2xl uppercase">
                  Pague usando o QR Code pelo app do seu banco de preferência ou
                  copie e cole abaixo no seu internet banking:
                </p>
                <div
                  className="bg-gray-100 mx-auto"
                  style={{ width: 170, height: 170 }}
                >
                  <img
                    className="w-full h-full"
                    src={`data:image/png;base64,${details?.payments?.pix_base64}`}
                    alt=""
                  />
                </div>
                <p className="text-[#666] font-alternative font-semibold leading-7 text-2xl uppercase">
                  Código Copia e Cola:
                </p>
                <div className="grid grid-cols-[1fr_auto] gap-3">
                  <span className="font-alternative text-primary-1 text-2xl font-semibold leading-7 truncate">
                    {details?.payments?.pix_copy_paste}
                  </span>
                  <IconButton
                    type="button"
                    icon={<IoCopyOutline />}
                    onClick={() =>
                      navigator.clipboard.writeText(
                        details?.payments?.pix_copy_paste ?? ""
                      )
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-5 mb-5 px-5">
                <Field label="CPF/CNPJ">
                  <Input
                    {...form.register("user_info.cpf_cnpj")}
                    onInput={mask(formatters.cpfCnpj)}
                  />
                </Field>
              </div>
            ))}
        </div>
      )}

      <div className="flex justify-between -mx-5 px-12 pt-5 pb-10 border-b-4 border-b-secondary-1">
        <div>
          <h3 className="text-2xl text-primary-1 font-semibold font-alternative uppercase mb-2">
            Produto
          </h3>
          <p className="font-alternative text-2xl font-semibold uppercase">
            {product.name}
          </p>
        </div>
        <div>
          <h3 className="text-2xl text-primary-1 font-semibold font-alternative uppercase mb-2">
            Total
          </h3>
          <p className="font-alternative text-2xl font-semibold uppercase">
            {product.value.toLocaleString(["pt-BR"], {
              style: "currency",
              currency: "BRL",
            })}
          </p>
        </div>
      </div>

      <footer className="flex items-center justify-between pt-6">
        <a
          href="https://www.asaas.com"
          target="_blank"
          rel="noreferer noopener"
          className="cursor-pointer"
        >
          <img src={asaasLogo} alt="" height="59" style={{ height: 59 }} />
        </a>

        {isPaymentFinished ? (
          <Button
            variant="alternative-1"
            className="h-10 w-60 max-w-full"
            asChild
          >
            <Link to="/">Voltar para o seu Painel</Link>
          </Button>
        ) : isPendingPixPayment ? (
          <p className="h-10 text-alternative-1 uppercase px-8 font-alternative text-base font-semibold">
            Aguardando pagamento...
          </p>
        ) : hasBillingType ? (
          <Button
            variant="alternative-1"
            className="h-10 w-60 max-w-full"
            loading={mutation.isPending}
          >
            {billingType === "pix" ? "Próximo" : "Realizar Pagamento"}
          </Button>
        ) : null}
      </footer>
    </form>
  );
}

const formatters = {
  cpfCnpj: (value) => {
    const digits = value.replace(/\D/g, "");
    const isCPF = digits.length <= 11;

    if (isCPF) {
      return digits
        .slice(0, 11)
        .replace(/(\d{3})(\d{0,3})?(\d{0,3})?(\d{0,2})?/, (_, p1, p2, p3, p4) =>
          [p1, p2, p3, p4]
            .filter(Boolean)
            .join(".")
            .replace(/\.(\d{2})$/, "-$1")
        );
    }

    return digits
      .slice(0, 14)
      .replace(
        /(\d{2})(\d{0,3})?(\d{0,3})?(\d{0,4})?(\d{0,2})?/,
        (_, p1, p2, p3, p4, p5) =>
          [p1, p2, p3, p4, p5]
            .filter(Boolean)
            .join(".")
            .replace(/\.(\d{4})\.(\d{2})$/, "/$1-$2")
      );
  },

  cep: (value) => {
    return value
      .replace(/\D/g, "")
      .slice(0, 8)
      .replace(/(\d{5})(\d{0,3})/, (_, p1, p2) => (p2 ? `${p1}-${p2}` : p1));
  },

  phone: (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 10) {
      return digits
        .replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3")
        .replace(/-$/, "");
    } else {
      return digits.replace(/(\d{2})(\d{1})(\d{4})(\d{4})/, "($1) $2$3-$4");
    }
  },
} satisfies Record<string, (value: string) => string>;
